import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ArrowLeft, Copy, Loader2, RefreshCw, Handshake, TrendingUp, MousePointerClick, UserPlus, CreditCard, Wallet, Percent, Link as LinkIcon, BadgeCheck } from "lucide-react";

import { useAuth } from "@/hooks/use-auth";
import { storePostLoginRedirect } from "@/lib/auth-redirect";
import { getMyAffiliateDashboard } from "@/lib/affiliates.functions";

export const Route = createFileRoute("/affiliate")({
  component: AffiliatePage,
  head: () => ({
    meta: [
      { title: "Painel do Afiliado · Routify" },
      { name: "description", content: "Acompanha o desempenho do teu programa de afiliado Routify: cliques, conversões e comissões." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

type Dashboard = Extract<Awaited<ReturnType<typeof getMyAffiliateDashboard>>, { ok: true }>;

const fmtMoney = (cents: number, currency = "EUR") =>
  new Intl.NumberFormat("pt-PT", { style: "currency", currency }).format((cents ?? 0) / 100);

const fmtPct = (v: number) => `${(v * 100).toFixed(1)}%`;

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const STATUS_LABEL: Record<string, string> = {
  pending: "Pendente",
  validating: "Em validação",
  approved: "Aprovada",
  paid: "Paga",
  canceled: "Cancelada",
  void: "Anulada",
};

const STATUS_CLASS: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  validating: "bg-blue-100 text-blue-800 border-blue-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  paid: "bg-green-600 text-white border-green-700",
  canceled: "bg-red-100 text-red-800 border-red-200",
  void: "bg-neutral-200 text-neutral-700 border-neutral-300",
};

function AffiliatePage() {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const loadDashboard = useServerFn(getMyAffiliateDashboard);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<Dashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      storePostLoginRedirect("/affiliate");
      navigate({ to: "/login" });
      return;
    }
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await loadDashboard();
        if (!alive) return;
        if (!res.ok) {
          setError(res.error === "not_an_affiliate" ? "not_an_affiliate" : "generic");
        } else {
          setData(res);
        }
      } catch {
        if (alive) setError("generic");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [authLoading, user, loadDashboard, navigate]);

  const refLink = useMemo(() => {
    if (!data) return "";
    const origin = typeof window !== "undefined" ? window.location.origin : "https://www.routify-tour.com";
    return `${origin}/?ref=${encodeURIComponent(data.affiliate.ref_slug)}`;
  }, [data]);

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado`);
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-500" />
      </div>
    );
  }

  if (error === "not_an_affiliate") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-neutral-200 rounded-2xl p-8 text-center shadow-sm">
          <Handshake className="w-10 h-10 mx-auto text-neutral-400 mb-3" />
          <h1 className="text-lg font-semibold text-neutral-900 mb-2">Ainda não és afiliado Routify</h1>
          <p className="text-sm text-neutral-600 mb-6">
            Este painel é exclusivo para parceiros do programa de afiliados. Se te interessa candidatar-te, contacta a equipa Routify.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline">
            <ArrowLeft className="w-4 h-4" /> Voltar ao início
          </Link>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-sm text-neutral-600">Não foi possível carregar o painel. Tenta novamente.</div>
      </div>
    );
  }

  const { affiliate, stats, commissions } = data;

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6">
        <div className="flex items-center justify-between gap-3">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900">
            <ArrowLeft className="w-4 h-4" /> Início
          </Link>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900"
          >
            <RefreshCw className="w-4 h-4" /> Atualizar
          </button>
        </div>

        <header className="bg-white border border-neutral-200 rounded-2xl p-5 sm:p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
              <Handshake className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-semibold text-neutral-900 truncate">
                Olá, {affiliate.name}
              </h1>
              <p className="text-sm text-neutral-600 mt-1">
                Painel do afiliado · {affiliate.active ? (
                  <span className="inline-flex items-center gap-1 text-emerald-700"><BadgeCheck className="w-3.5 h-3.5" /> Ativo</span>
                ) : (
                  <span className="text-red-600">Inativo</span>
                )}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
            <div className="rounded-xl border border-neutral-200 p-4">
              <div className="text-xs uppercase tracking-wide text-neutral-500 mb-1 flex items-center gap-1"><Percent className="w-3.5 h-3.5" /> Código promocional</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 font-mono text-base text-neutral-900 truncate">{affiliate.code}</code>
                <button
                  onClick={() => copy(affiliate.code, "Código")}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-neutral-100 hover:bg-neutral-200 text-neutral-700"
                >
                  <Copy className="w-3.5 h-3.5" /> Copiar
                </button>
              </div>
              <div className="text-xs text-neutral-500 mt-2">
                Desconto para o cliente: <strong className="text-neutral-800">{affiliate.discount_percent}%</strong> · A tua comissão: <strong className="text-neutral-800">{affiliate.commission_percent}%</strong>
              </div>
            </div>

            <div className="rounded-xl border border-neutral-200 p-4">
              <div className="text-xs uppercase tracking-wide text-neutral-500 mb-1 flex items-center gap-1"><LinkIcon className="w-3.5 h-3.5" /> Link de referência</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 font-mono text-xs sm:text-sm text-neutral-900 truncate">{refLink}</code>
                <button
                  onClick={() => copy(refLink, "Link")}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Copy className="w-3.5 h-3.5" /> Copiar
                </button>
              </div>
              <div className="text-xs text-neutral-500 mt-2">Partilha este link — atribuição válida 30 dias após o clique mais recente (cada novo clique renova a janela).</div>
            </div>
          </div>
        </header>

        {/* Traffic + conversions */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon={<MousePointerClick className="w-4 h-4" />} label="Cliques no link" value={stats.clicks.toLocaleString("pt-PT")} />
          <StatCard icon={<TrendingUp className="w-4 h-4" />} label="Visitas atribuídas" value={stats.visits.toLocaleString("pt-PT")} />
          <StatCard icon={<UserPlus className="w-4 h-4" />} label="Registos" value={stats.signups.toLocaleString("pt-PT")} />
          <StatCard icon={<CreditCard className="w-4 h-4" />} label="Assinaturas Premium" value={stats.purchases.toLocaleString("pt-PT")} />
        </section>

        {/* Financials (affiliate-only view) */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            icon={<Wallet className="w-4 h-4" />}
            label="Vendas atribuídas"
            value={fmtMoney(stats.totalSalesCents, stats.currency)}
            tone="neutral"
          />
          <StatCard
            icon={<Wallet className="w-4 h-4" />}
            label="Comissão pendente"
            value={fmtMoney(stats.pendingCents, stats.currency)}
            tone="amber"
          />
          <StatCard
            icon={<Wallet className="w-4 h-4" />}
            label="Comissão aprovada"
            value={fmtMoney(stats.approvedCents, stats.currency)}
            tone="emerald"
          />
          <StatCard
            icon={<Wallet className="w-4 h-4" />}
            label="Comissão paga"
            value={fmtMoney(stats.paidCents, stats.currency)}
            tone="green"
          />
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <StatCard
            icon={<TrendingUp className="w-4 h-4" />}
            label="Taxa de conversão (cliques → compra)"
            value={fmtPct(stats.conversionRate)}
            tone="blue"
          />
          <StatCard
            icon={<UserPlus className="w-4 h-4" />}
            label="Registos por 100 cliques"
            value={stats.clicks > 0 ? ((stats.signups / stats.clicks) * 100).toFixed(1) : "0"}
            tone="neutral"
          />
        </section>

        {/* Commission history */}
        <section className="bg-white border border-neutral-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-neutral-200">
            <h2 className="text-base font-semibold text-neutral-900">Histórico de comissões</h2>
            <p className="text-xs text-neutral-500 mt-0.5">Últimas {commissions.length} comissões atribuídas a ti.</p>
          </div>
          {commissions.length === 0 ? (
            <div className="p-8 text-center text-sm text-neutral-500">Ainda não há comissões registadas.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-neutral-600">
                  <tr>
                    <th className="text-left font-medium px-4 py-2">Data</th>
                    <th className="text-left font-medium px-4 py-2">Estado</th>
                    <th className="text-right font-medium px-4 py-2">Venda</th>
                    <th className="text-right font-medium px-4 py-2">Comissão</th>
                    <th className="text-left font-medium px-4 py-2 hidden sm:table-cell">Marco</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {commissions.map((c) => {
                    const milestone =
                      c.status === "paid" ? `Paga em ${fmtDate(c.paid_at)}`
                      : c.status === "approved" ? `Aprovada em ${fmtDate(c.approved_at)}`
                      : c.status === "canceled" ? `Cancelada em ${fmtDate(c.canceled_at)}`
                      : c.hold_until ? `Aprovação automática após ${fmtDate(c.hold_until)}`
                      : "—";
                    return (
                      <tr key={c.id} className="hover:bg-neutral-50">
                        <td className="px-4 py-2 text-neutral-700 whitespace-nowrap">{fmtDate(c.created_at)}</td>
                        <td className="px-4 py-2">
                          <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full border ${STATUS_CLASS[c.status] ?? "bg-neutral-100 text-neutral-700 border-neutral-200"}`}>
                            {STATUS_LABEL[c.status] ?? c.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right text-neutral-700 whitespace-nowrap">{fmtMoney(c.gross_cents, c.currency)}</td>
                        <td className="px-4 py-2 text-right font-medium text-neutral-900 whitespace-nowrap">{fmtMoney(c.commission_cents, c.currency)}</td>
                        <td className="px-4 py-2 text-neutral-500 hidden sm:table-cell whitespace-nowrap">{milestone}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <p className="text-xs text-neutral-500 text-center pb-6">
          Este painel mostra apenas o teu desempenho. Não expomos dados de outros afiliados, clientes ou finanças internas da Routify.
        </p>
      </div>
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  tone = "neutral",
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone?: "neutral" | "amber" | "emerald" | "green" | "blue";
}) {
  const toneClass = {
    neutral: "text-neutral-500",
    amber: "text-amber-600",
    emerald: "text-emerald-600",
    green: "text-green-600",
    blue: "text-blue-600",
  }[tone];
  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-4 shadow-sm">
      <div className={`flex items-center gap-1.5 text-xs uppercase tracking-wide ${toneClass}`}>
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <div className="mt-2 text-xl sm:text-2xl font-semibold text-neutral-900 tabular-nums">{value}</div>
    </div>
  );
}
